import React, { useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Search, ThumbsUp, ThumbsDown, Loader2, MessageCircle, Languages } from 'lucide-react';
import { TranscriptEntry, MessageRating } from '../../../hooks/useSessionDetail';
import { useAuth } from '../../../contexts/AuthContext';
import { canRate as canRateRole } from '../../../utils/roles';
import { api } from '../../role-play/apiService';
import { SUPPORTED_LANGUAGES } from '../../../utils/languages';
import { uiLocale } from '../../../i18n/language';

interface TranscriptSectionProps {
  conversations: TranscriptEntry[];
  /** The language the session was held in; lines are shown translated when the UI's differs. */
  sessionLanguage?: string;
  studentProfileName: string;
  userName: string;
  transcriptionId?: string;
  onConversationUpdate: (index: number, update: Partial<TranscriptEntry>) => void;
}

const TranscriptSection: React.FC<TranscriptSectionProps> = ({
  conversations,
  sessionLanguage,
  studentProfileName,
  userName,
  transcriptionId,
  onConversationUpdate,
}) => {
  const { t } = useTranslation();
  const [showOriginal, setShowOriginal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [feedbackOpen, setFeedbackOpen] = useState<number | null>(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [submittingIndices, setSubmittingIndices] = useState<Set<number>>(new Set());
  const { userRole } = useAuth();

  const canRate = canRateRole(userRole);

  // A reader whose UI language differs from the session's reads the transcript translated, and
  // can switch back to the exact words — what a reviewer judges, since a translation can soften them.
  const uiLanguage = uiLocale();
  const translated =
    !!sessionLanguage && uiLanguage !== sessionLanguage && conversations.some(c => c.translations?.[uiLanguage]);
  const showTranslation = translated && !showOriginal;
  const sessionLanguageLabel =
    SUPPORTED_LANGUAGES.find(option => option.code === sessionLanguage)?.label ?? sessionLanguage;

  const filteredConversations = useMemo(() => {
    const shown = conversations.map((c, i) => ({
      ...c,
      text: (showTranslation && c.translations?.[uiLanguage]) || c.text,
      originalIndex: i,
    }));
    if (!searchTerm.trim()) return shown;
    const term = searchTerm.toLowerCase();
    return shown.filter(c => c.text?.toLowerCase().includes(term));
  }, [conversations, searchTerm, showTranslation, uiLanguage]);

  const getSpeakerName = (speaker: string) => {
    return speaker === 'bot' ? (studentProfileName || t('feedback.aiStudent')) : (userName || t('common.trainer'));
  };

  const addSubmitting = (index: number) =>
    setSubmittingIndices(prev => new Set(prev).add(index));
  const removeSubmitting = (index: number) =>
    setSubmittingIndices(prev => { const next = new Set(prev); next.delete(index); return next; });

  const handleRating = async (index: number, rating: boolean) => {
    if (!canRate || !transcriptionId || submittingIndices.has(index)) return;
    const entry = conversations[index];
    const currentRating = entry?.message_rating?.upvote;

    if (currentRating === rating && entry?.message_rating?.id) {
      addSubmitting(index);
      try {
        await api.delete(`/message-rating/${entry.message_rating.id}`);
        onConversationUpdate(index, { message_rating: undefined });
      } catch (err) {
        console.error('Error deleting rating:', err);
      } finally {
        removeSubmitting(index);
      }
      return;
    }

    if (entry?.message_rating?.id) {
      try {
        await api.delete(`/message-rating/${entry.message_rating.id}`);
        onConversationUpdate(index, { message_rating: undefined });
      } catch (err) {
        console.error('Error deleting rating:', err);
        return;
      }
    }

    if (!rating) {
      setFeedbackOpen(index);
      setFeedbackComment('');
      return;
    }

    addSubmitting(index);
    try {
      const response = await api.post<MessageRating>('/message-rating', {
        roleplay_transcription_id: transcriptionId,
        message_index: index,
        upvote: true,
      });
      onConversationUpdate(index, { message_rating: response });
    } catch (err) {
      console.error('Error submitting rating:', err);
    } finally {
      removeSubmitting(index);
    }
  };

  const submitFeedback = async (index: number) => {
    if (!transcriptionId || submittingIndices.has(index)) return;
    addSubmitting(index);
    try {
      const response = await api.post<MessageRating>('/message-rating', {
        roleplay_transcription_id: transcriptionId,
        message_index: index,
        upvote: false,
        comment: feedbackComment,
      });
      onConversationUpdate(index, { message_rating: response });
      setFeedbackOpen(null);
      setFeedbackComment('');
    } catch (err) {
      console.error('Error submitting feedback:', err);
    } finally {
      removeSubmitting(index);
    }
  };

  if (!conversations.length) {
    return (
      <div className="transcript-section empty">
        <p>{t('feedback.noTranscriptForSession')}</p>
      </div>
    );
  }

  const isFiltering = searchTerm.trim().length > 0;

  return (
    <div className="transcript-section" id="tour-session-transcript">
      <div className="transcript-search" id="tour-transcript-search">
        <Search size={14} />
        <input
          type="text"
          placeholder={t('feedback.searchTranscriptPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {translated && (
        <div className="transcript-translation-bar">
          <Languages size={13} aria-hidden="true" />
          <span>
            {showOriginal
              ? t('feedback.transcriptOriginal', { language: sessionLanguageLabel })
              : t('feedback.transcriptTranslatedFrom', { language: sessionLanguageLabel })}
          </span>
          <button type="button" className="transcript-translation-toggle" onClick={() => setShowOriginal(v => !v)}>
            {showOriginal ? t('feedback.showTranslation') : t('feedback.showOriginal')}
          </button>
        </div>
      )}

      <div className="transcript-messages">
        {/* Conversation start marker */}
        {!isFiltering && (
          <div className="transcript-marker start">
            <MessageCircle size={13} />
            <span>
              <Trans
                i18nKey="feedback.conversationBetween"
                values={{
                  student: studentProfileName || t('common.student'),
                  user: userName || t('common.trainer'),
                  count: conversations.length,
                }}
                components={{ bold: <strong /> }}
              />
            </span>
          </div>
        )}

        {filteredConversations.map((entry) => (
          <div
            key={entry.originalIndex}
            className={`transcript-msg ${entry.speaker === 'bot' ? 'bot' : 'user'}`}
          >
            <div className="msg-row">
              <span className={`msg-speaker ${entry.speaker}`}>
                {getSpeakerName(entry.speaker)}
              </span>
              <span className="msg-text">{entry.text}</span>
            </div>

            {canRate && (
              <div className="msg-actions">
                <button
                  className={`msg-rate-pill ${entry.message_rating?.upvote === true ? 'active-up' : ''}`}
                  onClick={() => handleRating(entry.originalIndex, true)}
                  disabled={submittingIndices.has(entry.originalIndex)}
                >
                  <ThumbsUp size={11} /> {t('feedback.good')}
                </button>
                <button
                  className={`msg-rate-pill ${entry.message_rating?.upvote === false ? 'active-down' : ''}`}
                  onClick={() => handleRating(entry.originalIndex, false)}
                  disabled={submittingIndices.has(entry.originalIndex)}
                >
                  <ThumbsDown size={11} /> {t('feedback.flag')}
                </button>
              </div>
            )}

            {entry.message_rating?.comment && feedbackOpen !== entry.originalIndex && (
              <div className="msg-comment">
                {entry.message_rating.comment}
              </div>
            )}

            {feedbackOpen === entry.originalIndex && (
              <div className="msg-feedback-form">
                <textarea
                  value={feedbackComment}
                  onChange={(e) => setFeedbackComment(e.target.value)}
                  placeholder={t('feedback.whatNeedsImprovement')}
                  rows={2}
                  disabled={submittingIndices.has(entry.originalIndex)}
                />
                <div className="msg-feedback-actions">
                  <button className="cancel-btn" onClick={() => setFeedbackOpen(null)} disabled={submittingIndices.has(entry.originalIndex)}>
                    {t('common.cancel')}
                  </button>
                  <button
                    className="submit-btn"
                    onClick={() => submitFeedback(entry.originalIndex)}
                    disabled={!feedbackComment.trim() || submittingIndices.has(entry.originalIndex)}
                  >
                    {submittingIndices.has(entry.originalIndex) ? <Loader2 size={12} className="spin" /> : t('common.submit')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Conversation end marker */}
        {!isFiltering && (
          <div className="transcript-marker end">
            <span>{t('feedback.endOfConversation')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TranscriptSection;
